import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FiEye, FiEyeOff } from "react-icons/fi";
import bynLogo from "./byn-logo.jpeg";

const API_BASE = process.env.REACT_APP_API_BASE; // ✅ unified base

const Signup = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    console.log("🟢 Signup Page Mounted");
    console.log("API_BASE:", API_BASE);
  }, []);

  /* =========================================================
     🧮 Password Validation
  ========================================================= */
  const validatePassword = (pass) => {
    if (pass.length < 6) return "Must be at least 6 characters.";
    if (!/[A-Z]/.test(pass)) return "Must include one uppercase letter.";
    if (!/[!@#$%^&*]/.test(pass)) return "Must include one special character.";
    return "";
  };

  /* =========================================================
     ✍️ Handle Signup
  ========================================================= */
  const handleSignup = async (e) => {
    e.preventDefault();
    setMessage("");

    const passwordCheck = validatePassword(password);
    if (passwordCheck) {
      setPasswordError(passwordCheck);
      return;
    }

    if (!email || !password || !confirmPassword) {
      setMessage("All fields are required.");
      return;
    }

    if (password !== confirmPassword) {
      setMessage("Passwords do not match.");
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/api/customers/signup`, {
        email,
        password,
      });

      if (response.status === 201) {
        const { token, _id } = response.data;

        localStorage.setItem("userEmail", email);
        localStorage.setItem("userToken", token);
        localStorage.setItem("customerId", _id);

        const profileRes = await axios.get(
          `${API_BASE}/api/customers/user-profile/${email}`
        );
        const profile = profileRes.data;

        setMessage("Signup successful! Redirecting...");
        setTimeout(() => {
          if (!profile.name || !profile.lastName) return navigate("/complete-profile");
          if (!profile.phone) return navigate("/verify-phone");
          if (!profile.address) return navigate("/set-address");
          if (!profile.profilePicture) return navigate("/upload-photo");
          navigate("/terms");
        }, 800);
      } else {
        setMessage("Signup failed.");
      }
    } catch (error) {
      console.error("🚨 Signup Error:", error);
      if (error.response?.data?.message?.includes("exists")) {
        setMessage("This email is already registered.");
      } else {
        setMessage("Something went wrong. Please try again.");
      }
    }
  };

  /* =========================================================
     🎨 UI (MATCHED TO LOGIN)
  ========================================================= */
  return (
    <div className="min-h-screen bg-white px-6 py-12">
      <div className="max-w-md mx-auto space-y-6">

        {/* 🔥 BIG CENTERED LOGO */}
        <div className="flex justify-center mb-10">
          <img
            src={bynLogo}
            alt="Book Your Need"
            className="w-48 h-48 object-contain"
          />
        </div>

        <form onSubmit={handleSignup} className="space-y-4">
          {/* Email */}
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          {/* Password */}
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordError(validatePassword(e.target.value));
              }}
              required
              className="w-full px-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <span
              className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer text-gray-400"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </span>
            {passwordError && (
              <p className="text-xs text-red-500 mt-1">{passwordError}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="relative">
            <input
              type={showConfirm ? "text" : "password"}
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <span
              className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer text-gray-400"
              onClick={() => setShowConfirm(!showConfirm)}
            >
              {showConfirm ? <FiEyeOff /> : <FiEye />}
            </span>
          </div>

          <button
            type="submit"
            disabled={!!passwordError}
            className={`w-full py-3 rounded-full font-semibold transition ${
              passwordError
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:opacity-90 active:scale-95"
            }`}
          >
            Sign Up
          </button>
        </form>

        {message && (
          <p className="text-sm text-red-500 text-center">{message}</p>
        )}

        <p className="text-sm text-gray-500 text-center">
          Already have an account?{" "}
          <span
            onClick={() => navigate("/login")}
            className="text-indigo-600 font-semibold hover:underline cursor-pointer"
          >
            Log In
          </span>
        </p>
      </div>
    </div>
  );
};

export default Signup;
